import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { IBucketProduct } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

// ClickCollectOrderMin/Max are per ARTICLE, while the cart can hold several lines of one
// article (different kitchenInfo / template). Everything here therefore works on the summed
// count across lines, rounded to 3 decimals — weight counts move in 0.1 steps and raw float
// sums (0.1 + 0.2) must not read as "over the bound".

export type ArticleBounds = {
  min: number | null
  max: number | null
}

export const roundCount = (value: number): number =>
  Math.round(value * 1000) / 1000

// 0 and negative mean "not set", mirroring AmountHandler's maxValue semantics
const toBound = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const getArticleBounds = (
  product?: Pick<
    IProduct,
    'ClickCollectOrderMin' | 'ClickCollectOrderMax'
  > | null
): ArticleBounds => ({
  min: toBound(product?.ClickCollectOrderMin),
  max: toBound(product?.ClickCollectOrderMax),
})

export const otherLinesTotal = (
  lines: IBucketProduct[] = [],
  productId: number,
  excludeUniqueId?: string
): number =>
  roundCount(
    lines
      .filter(
        (line) =>
          line.productId === productId && line.uniqueId !== excludeUniqueId
      )
      .reduce((sum, line) => sum + (Number(line.count) || 0), 0)
  )

// The count still available to ONE line of the article: max minus what the other lines
// already hold. null = no cap.
export const articleHeadroom = (
  bounds: ArticleBounds,
  lines: IBucketProduct[] = [],
  productId: number,
  excludeUniqueId?: string
): number | null =>
  bounds.max === null
    ? null
    : Math.max(
        0,
        roundCount(
          bounds.max - otherLinesTotal(lines, productId, excludeUniqueId)
        )
      )

// Cap a requested line count so the article total never exceeds the max. Never raises the
// count (no silent fill-up to a min) and never turns a positive request into a removal —
// a request with zero headroom keeps the line's previous count (the caller's UI should not
// have offered the step in the first place).
export const clampLineCount = (
  requested: number,
  previous: number,
  bounds: ArticleBounds,
  lines: IBucketProduct[] = [],
  productId: number,
  excludeUniqueId?: string
): number => {
  const headroom = articleHeadroom(bounds, lines, productId, excludeUniqueId)
  if (headroom === null) return requested
  if (requested <= headroom) return requested
  // a decrease is always allowed, even above the headroom — it reduces the violation
  // (a stale cart whose other lines already fill the max must stay editable downwards)
  if (requested < previous) return requested
  return headroom > 0 ? headroom : previous
}

export type OutOfBoundsArticle = {
  productId: number
  total: number
  min: number | null
  max: number | null
  lines: IBucketProduct[]
}

// Lines whose article total violates the LIVE catalog bounds — the check for persisted
// carts (the cart stores no bounds; the catalog is the only source, so an admin change
// is picked up on the next load). An article missing from the catalog is not judged here;
// the template/sold-out checkpoints own that case.
export const findOutOfBoundsArticles = (
  lines: IBucketProduct[] = [],
  resolveProduct: (
    productId: number
  ) =>
    | Pick<IProduct, 'ClickCollectOrderMin' | 'ClickCollectOrderMax' | 'id'>
    | null
    | undefined
): OutOfBoundsArticle[] => {
  const byProduct = new Map<number, IBucketProduct[]>()
  lines.forEach((line) => {
    if (!Number.isFinite(Number(line.productId))) return
    const group = byProduct.get(line.productId) || []
    group.push(line)
    byProduct.set(line.productId, group)
  })

  const violations: OutOfBoundsArticle[] = []
  byProduct.forEach((group, productId) => {
    const product = resolveProduct(productId)
    if (!product) return
    const bounds = getArticleBounds(product)
    if (bounds.min === null && bounds.max === null) return
    const total = otherLinesTotal(group, productId)
    if (
      (bounds.min !== null && total < bounds.min) ||
      (bounds.max !== null && total > bounds.max)
    ) {
      violations.push({
        productId,
        total,
        min: bounds.min,
        max: bounds.max,
        lines: group,
      })
    }
  })
  return violations
}

export const QUANTITY_REFUSAL_CODE = 'QUANTITY_OUT_OF_BOUNDS'

type RefusalLike = { code?: unknown; data?: { productIds?: unknown } }

export const isQuantityRefusal = (error: unknown): error is RefusalLike =>
  (error as RefusalLike | null | undefined)?.code === QUANTITY_REFUSAL_CODE
